/** App-owned cue lifecycle. The room package knows nothing about these sounds. */
import { RING_MAX_AGE_MS } from "@repo/conferencing";
import { callStore } from "@repo/conferencing/components";
import { callAnswerStore } from "./call-answer-store";
import { callIdFromRoomKey } from "./call-room";
import { callSoundDecision } from "./call-sound-decision";
import { DAMPED_FALL, MARIMBA_LIFT, playOnce, RECEIVER_DOWN } from "./call-sound-synthesis";
import type { RingDelivery } from "./incoming-ring";

let outgoing: { callId: string; stop: () => void } | undefined;

function state() {
  const active = callStore.get().context.active;
  return { ...callAnswerStore.get().context, activeCallId: active && callIdFromRoomKey(active.key) };
}

export function stopOutgoingRingback(callId: string): void {
  if (outgoing?.callId !== callId) return;
  outgoing.stop();
  outgoing = undefined;
}

export function startOutgoingRingback(callId: string, startedAt: number): void {
  if (outgoing) stopOutgoingRingback(outgoing.callId);
  const decision = () => callSoundDecision({ kind: "ringback", callId, ageMs: Date.now() - startedAt }, state());
  if (decision() !== "ringback") return;

  let cancelPhrase = () => {};
  const play = () => {
    // Timers can wake late in a background tab. Recheck the deadline before
    // scheduling, even if the expiry callback has not had its turn yet.
    if (decision() !== "ringback") {
      stopOutgoingRingback(callId);
      return;
    }
    cancelPhrase();
    cancelPhrase = playOnce(MARIMBA_LIFT);
  };
  const loop = setInterval(play, MARIMBA_LIFT.period * 1000);
  const expiry = setTimeout(() => stopOutgoingRingback(callId), Math.max(0, startedAt + RING_MAX_AGE_MS - Date.now()));
  const room = callStore.subscribe(() => {
    if (decision() !== "ringback") stopOutgoingRingback(callId);
  });
  outgoing = {
    callId,
    stop: () => {
      clearInterval(loop);
      clearTimeout(expiry);
      room.unsubscribe();
      cancelPhrase();
    },
  };
  play();
}

/** Decide before closing the room: afterward a stale decline looks identical. */
export function soundForDelivery(delivery: RingDelivery): void {
  const cue = callSoundDecision(delivery, state());
  if (delivery.kind !== "unanswered" || cue === "none") return;
  stopOutgoingRingback(delivery.callId);
  if (cue === "declined") playOnce(DAMPED_FALL);
}

export function playCallLeave(): void {
  if (callSoundDecision({ kind: "leave" }, state()) === "hangUp") playOnce(RECEIVER_DOWN);
}
